'use client'

import Link from 'next/link'
import React, { useEffect } from 'react'

import { Button } from '@/components/ui/button'
import { localizePath } from '@/lib/i18n/config'
import { useLocale } from '@/lib/i18n/useLocale'
import { useMessages } from '@/lib/i18n/useMessages'

type ErrorPageProps = {
  error: Error & { digest?: string }
  reset: () => void
}

export default function FrontendError({ error, reset }: ErrorPageProps) {
  const locale = useLocale()
  const messages = useMessages()
  const copy = messages.errorPage
  const isDev = process.env.NODE_ENV === 'development'

  useEffect(() => {
    console.error(error)
  }, [error])

  return (
    <main className="flex flex-1 flex-col bg-cream focus:outline-none" id="main-content" tabIndex={-1}>
      <div className="container flex flex-1 flex-col items-center justify-center py-28 text-center">
        <p className="text-eyebrow text-dark-green">{copy.eyebrow}</p>

        <h1 className="text-title mt-4 text-bluetext">{copy.title}</h1>

        <p className="font-outfit mt-4 max-w-xl text-base text-bluetext/80">
          {isDev ? error.message : copy.description}
        </p>

        {error.digest ? (
          <p className="font-outfit mt-2 text-sm text-bluetext/60">
            {copy.digestLabel}: {error.digest}
          </p>
        ) : null}

        {isDev && error.digest === 'DYNAMIC_SERVER_USAGE' ? (
          <p className="font-outfit mt-4 max-w-lg text-sm text-bluetext/70">
            This route needs dynamic server rendering. Rebuild and redeploy after pulling the latest
            code.
          </p>
        ) : null}

        {isDev && error.digest !== 'DYNAMIC_SERVER_USAGE' ? (
          <p className="font-outfit mt-4 max-w-lg text-sm text-bluetext/70">
            If you are running locally, make sure Postgres is up (
            <code className="rounded bg-bluetext/5 px-1.5 py-0.5">docker compose up postgres -d</code>
            ), run{' '}
            <code className="rounded bg-bluetext/5 px-1.5 py-0.5">npm run db:sync</code>, then restart
            the server.
          </p>
        ) : null}

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button onClick={() => reset()} type="button" variant="default">
            {copy.tryAgain}
          </Button>
          <Button asChild variant="outline">
            <Link href={localizePath('/', locale)}>{messages.common.goHome}</Link>
          </Button>
        </div>
      </div>
    </main>
  )
}
